import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';

const CONTAINER = 'mx-auto w-full max-w-[1240px] px-4 md:px-8 lg:px-10';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db6f4222sgkaik1unegg';

const INPUT_CLASS =
  'h-12 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none';

const CONTACT_INFO = [
  { icon: 'ri-mail-line', label: '联系邮箱', value: 'contact@rihansubtitles.example' },
  { icon: 'ri-time-line', label: '回复时间', value: '工作日 24 小时内答复' },
  { icon: 'ri-group-line', label: '字幕组合作', value: '欢迎来函洽谈内容合作' },
];

export default function ContactPage() {
  usePageMeta(
    '联系我们 - 日韩中文字幕',
    '通过联系表单与我们取得联系，反馈内容建议、字幕纠错或合作洽谈，我们会尽快回复。'
  );

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    formData.delete('website_alt');

    if (honeypot) {
      setStatus('success');
      setMessage('留言已提交，感谢你的反馈！');
      form.reset();
      return;
    }

    setStatus('submitting');
    setMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        setMessage('留言提交成功，我们会尽快通过邮箱与你联系。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(
          serverMsg
            ? `提交失败：${serverMsg}`
            : '提交失败，请稍后重试，或直接发送邮件联系我们。'
        );
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后重试，或直接发送邮件联系我们。');
    }
  };

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className={`${CONTAINER} py-8 md:py-12`}>
          <Breadcrumb items={[{ label: '主页', to: '/' }, { label: '联系我们' }]} />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论你是想反馈字幕信息、提出内容建议，还是洽谈字幕组合作，都欢迎给我们留言，我们会认真阅读每一条消息。
          </p>
        </div>
      </section>

      <section className={`${CONTAINER} py-12 md:py-16`}>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr] lg:gap-12">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">给我们留言</h2>
            <p className="mt-2 text-sm text-foreground-500">
              填写以下信息提交，带 <span className="text-accent-600">*</span> 的为必填项。
            </p>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 space-y-5"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground-700">
                    姓名 <span className="text-accent-600">*</span>
                  </span>
                  <input
                    type="text"
                    name="name"
                    required
                    maxLength={60}
                    placeholder="请输入你的称呼"
                    className={INPUT_CLASS}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground-700">
                    邮箱 <span className="text-accent-600">*</span>
                  </span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="用于接收我们的回复"
                    className={INPUT_CLASS}
                  />
                </label>
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground-700">
                  留言内容 <span className="text-accent-600">*</span>
                </span>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="请描述你遇到的问题、建议或合作意向（最多 500 字）"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none"
                ></textarea>
                <span className="text-xs text-foreground-400">留言内容最多 500 个字符。</span>
              </label>

              <input
                type="text"
                name="website_alt"
                className="form-antibot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交留言
                    </>
                  )}
                </button>
                <p className="text-xs text-foreground-400">
                  本站为信息展示平台，留言仅用于沟通，不会用于其他用途。
                </p>
              </div>

              {status === 'success' && message && (
                <div className="flex items-start gap-2.5 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-accent-600"></i>
                  <span>{message}</span>
                </div>
              )}
              {status === 'error' && message && (
                <div className="flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-900">
                  <i className="ri-error-warning-line mt-0.5 text-base text-primary-600"></i>
                  <span>{message}</span>
                </div>
              )}
            </form>
          </div>

          <aside className="space-y-4">
            {CONTACT_INFO.map((info) => (
              <div key={info.label} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${info.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">{info.label}</p>
                  <p className="mt-1 break-words text-sm font-medium text-foreground-900">{info.value}</p>
                </div>
              </div>
            ))}
            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <p className="flex items-center gap-2 text-sm font-semibold text-accent-800">
                <i className="ri-lightbulb-line"></i>
                温馨提示
              </p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                本站不提供影视文件与字幕文件下载，如需查找字幕信息，请在站内搜索或浏览对应分类页面。提交留言后，请留意邮箱回复。
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}